import { cloneElement, HTMLAttributes, ReactElement, useEffect, useState } from 'react';
import { Dropdown } from 'antd';

import { FLOATING_DROPDOWN_OVERLAY_STYLE } from '@/shared/components/dropdowns/DropdownPanel';

import ChangeDetailsPanel from '../ChangeDetailsPanel';
import type { PreviewChange } from '../changes';

interface ChangeTriggerAttributes extends HTMLAttributes<HTMLElement> {
    'data-resource-change-trigger'?: string;
}

interface ChangeDetailsDropdownProps {
    changes: PreviewChange[];
    children: ReactElement<ChangeTriggerAttributes>;
    enabled?: boolean;
}

/** Hover and keyboard review without adding a layout wrapper around the native resource. */
const ChangeDetailsDropdown = ({ changes, children, enabled = true }: ChangeDetailsDropdownProps) => {
    const [open, setOpen] = useState(false);
    const [nestedTrigger, setNestedTrigger] = useState(false);
    useEffect(() => {
        if (!enabled) {
            setOpen(false);
        }
    }, [enabled]);

    return (
        <Dropdown
            destroyOnHidden
            onOpenChange={(nextOpen) => setOpen(enabled && nextOpen && !nestedTrigger)}
            open={enabled && open}
            overlayStyle={FLOATING_DROPDOWN_OVERLAY_STYLE}
            placement='bottomLeft'
            popupRender={() => <ChangeDetailsPanel changes={changes} />}
            trigger={['hover']}
        >
            {cloneElement(children, {
                'data-resource-change-trigger': 'true',
                onMouseOver: (event) => {
                    children.props.onMouseOver?.(event);
                    const isNested =
                        event.target instanceof Element &&
                        event.target.closest('[data-resource-change-trigger]') !== event.currentTarget;
                    setNestedTrigger(isNested);
                    if (isNested) {
                        setOpen(false);
                    } else if (nestedTrigger) {
                        setOpen(enabled);
                    }
                },
                onFocus: (event) => {
                    children.props.onFocus?.(event);
                    setOpen(enabled && event.target === event.currentTarget);
                },
                onBlur: (event) => {
                    children.props.onBlur?.(event);
                    if (!event.currentTarget.contains(event.relatedTarget)) {
                        setOpen(false);
                    }
                },
                onKeyDown: (event) => {
                    children.props.onKeyDown?.(event);
                    if (event.key === 'Escape') {
                        setOpen(false);
                    }
                },
            })}
        </Dropdown>
    );
};

export default ChangeDetailsDropdown;
